import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, DollarSign, TrendingUp, TrendingDown, Settings, 
  Eye, EyeOff, GripVertical, Maximize2, Minimize2, RotateCcw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { 
  hasPermission, 
  formatCurrency, 
  parseAmount,
  filterTransactionsByPermission,
  canViewZavatsoa 
} from '../../utils/helpers';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

import DashboardAnnouncements from '../announcements/DashboardAnnouncements';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Helpers purs pour les graphiques (pour mieux mémoïser)
const buildMonthlyData = (donations, expenses) => {
  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  const revenueByMonth = new Array(12).fill(0);
  const expenseByMonth = new Array(12).fill(0);

  donations.forEach(d => {
    if (d.date) {
      const date = new Date(d.date);
      if (!isNaN(date.getTime())) {
        const month = date.getMonth();
        revenueByMonth[month] += parseAmount(d.amount);
      }
    }
  });

  expenses.forEach(e => {
    if (e.date) {
      const date = new Date(e.date);
      if (!isNaN(date.getTime())) {
        const month = date.getMonth();
        expenseByMonth[month] += parseAmount(e.amount);
      }
    }
  });

  return { months, revenueByMonth, expenseByMonth };
};

const buildCategoryData = (expenses) => {
  const categories = {};
  expenses.forEach(e => {
    const cat = e.category || 'Autre';
    const amount = parseAmount(e.amount);
    categories[cat] = (categories[cat] || 0) + amount;
  });
  return categories;
};

const DashboardView = ({ onNavigate }) => {
  const { currentUser } = useAuth();
  const { data } = useData();

  // État des widgets
  const [widgets, setWidgets] = useState(() => {
    const saved = localStorage.getItem('dashboardWidgets');
    return saved ? JSON.parse(saved) : {
      stats: { visible: true, size: 'normal', order: 0 },
      revenueChart: { visible: true, size: 'large', order: 1 },
      expenseChart: { visible: true, size: 'normal', order: 2 },
      categoryChart: { visible: true, size: 'normal', order: 3 },
      recentRevenue: { visible: true, size: 'normal', order: 4 },
      recentExpense: { visible: true, size: 'normal', order: 5 }
    };
  });

  const [editMode, setEditMode] = useState(false);

  // Sauvegarder les widgets dans localStorage
  useEffect(() => {
    localStorage.setItem('dashboardWidgets', JSON.stringify(widgets));
  }, [widgets]);

  // 🔎 Transactions visibles (filtrées selon les permissions)
  const visibleDonations = useMemo(
    () => filterTransactionsByPermission(data.donations || [], currentUser),
    [data.donations, currentUser]
  );

  const visibleExpenses = useMemo(
    () => filterTransactionsByPermission(data.expenses || [], currentUser),
    [data.expenses, currentUser]
  );

  // Totaux visibles (sans Zavatsoa pour certains rôles)
  const { totalRevenues, totalExpenses, realBalance } = useMemo(() => {
    const totalRevenues = visibleDonations.reduce(
      (sum, d) => sum + parseAmount(d.amount),
      0
    );
    const totalExpenses = visibleExpenses.reduce(
      (sum, e) => sum + parseAmount(e.amount),
      0
    );

    // 🔒 Totaux RÉELS (toutes donations/dépenses, y compris Zavatsoa)
    const realTotalRevenues = (data.donations || []).reduce(
      (sum, d) => sum + parseAmount(d.amount),
      0
    );
    const realTotalExpenses = (data.expenses || []).reduce(
      (sum, e) => sum + parseAmount(e.amount),
      0
    );

    return {
      totalRevenues,
      totalExpenses,
      realBalance: realTotalRevenues - realTotalExpenses
    };
  }, [visibleDonations, visibleExpenses, data.donations, data.expenses]);

  // Solde affiché = solde réel
  const balance = realBalance;

  const totalMembers = data.members?.length || 0;
  const activeMembers = data.members?.filter(m => m.status === 'Actif').length || 0;

  // Données mensuelles mémoïsées
  const monthlyData = useMemo(
    () => buildMonthlyData(visibleDonations, visibleExpenses),
    [visibleDonations, visibleExpenses]
  );

  // Données catégories mémoïsées
  const categoryData = useMemo(
    () => buildCategoryData(visibleExpenses),
    [visibleExpenses]
  );

  // Graphique revenus/dépenses
  const lineChartData = useMemo(
    () => ({
      labels: monthlyData.months,
      datasets: [
        {
          label: 'Revenus',
          data: monthlyData.revenueByMonth,
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Dépenses',
          data: monthlyData.expenseByMonth,
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    }),
    [monthlyData]
  );

  const lineChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value) => formatCurrency(value)
          }
        }
      }
    }),
    []
  );

  // Graphique par catégorie
  const doughnutChartData = useMemo(
    () => ({
      labels: Object.keys(categoryData),
      datasets: [
        {
          data: Object.values(categoryData),
          backgroundColor: [
            'rgba(59, 130, 246, 0.8)',
            'rgba(239, 68, 68, 0.8)',
            'rgba(34, 197, 94, 0.8)',
            'rgba(251, 191, 36, 0.8)',
            'rgba(168, 85, 247, 0.8)',
            'rgba(236, 72, 153, 0.8)'
          ],
          borderColor: [
            'rgb(59, 130, 246)',
            'rgb(239, 68, 68)',
            'rgb(34, 197, 94)',
            'rgb(251, 191, 36)',
            'rgb(168, 85, 247)',
            'rgb(236, 72, 153)'
          ],
          borderWidth: 2
        }
      ]
    }),
    [categoryData]
  );

  const doughnutChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.label || '';
              const value = formatCurrency(context.parsed);
              return `${label}: ${value}`;
            }
          }
        }
      }
    }),
    []
  );

  // Fonctions de gestion des widgets
  const toggleVisibility = (widgetId) => {
    setWidgets(prev => ({
      ...prev,
      [widgetId]: { ...prev[widgetId], visible: !prev[widgetId].visible }
    }));
  };

  const toggleSize = (widgetId) => {
    setWidgets(prev => ({
      ...prev,
      [widgetId]: {
        ...prev[widgetId],
        size: prev[widgetId].size === 'normal' ? 'large' : 'normal'
      }
    }));
  };

  const resetLayout = () => {
    if (!window.confirm('Réinitialiser la disposition du tableau de bord ?')) {
      return;
    }
    const defaultWidgets = {
      stats: { visible: true, size: 'normal', order: 0 },
      revenueChart: { visible: true, size: 'large', order: 1 },
      expenseChart: { visible: true, size: 'normal', order: 2 },
      categoryChart: { visible: true, size: 'normal', order: 3 },
      recentRevenue: { visible: true, size: 'normal', order: 4 },
      recentExpense: { visible: true, size: 'normal', order: 5 }
    };
    setWidgets(defaultWidgets);
  };

  // Composant Widget wrapper
  const Widget = ({ id, title, children, className = '' }) => {
    const widget = widgets[id];
    if (!widget?.visible) return null;

    const sizeClass = widget.size === 'large' ? 'col-span-1 md:col-span-2' : 'col-span-1';

    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${sizeClass} ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white">{title}</h3>
          {editMode && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggleSize(id)}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={widget.size === 'large' ? 'Réduire' : 'Agrandir'}
              >
                {widget.size === 'large' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button
                onClick={() => toggleVisibility(id)}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-red-600"
                title="Masquer"
              >
                <EyeOff size={16} />
              </button>
              <GripVertical size={16} className="text-gray-400 cursor-move" />
            </div>
          )}
        </div>
        {children}
      </div>
    );
  };

  return (
    <div>
      {/* Header avec boutons */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
            Tableau de bord
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Vue d'ensemble de l'église
          </p>
        </div>

        {/* Boutons de personnalisation */}
        <div className="flex gap-2">
          <button
            onClick={() => setEditMode(!editMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              editMode
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            <Settings size={18} />
            {editMode ? 'Terminer' : 'Personnaliser'}
          </button>

          {editMode && (
            <button
              onClick={resetLayout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
            >
              <RotateCcw size={18} />
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Mode édition : panneau de contrôle */}
      {editMode && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">
            Widgets disponibles
          </h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(widgets).map(([id, widget]) => (
              <button
                key={id}
                onClick={() => toggleVisibility(id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  widget.visible
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-2 border-green-500'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-gray-300 dark:border-gray-600'
                }`}
              >
                {widget.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                {id === 'stats' && 'Statistiques'}
                {id === 'revenueChart' && 'Graphique Revenus/Dépenses'}
                {id === 'expenseChart' && 'Évolution Mensuelle'}
                {id === 'categoryChart' && 'Dépenses par Catégorie'}
                {id === 'recentRevenue' && 'Revenus Récents'}
                {id === 'recentExpense' && 'Dépenses Récentes'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Message informatif Zavatsoa */}
      {!canViewZavatsoa(currentUser) && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-4 mb-6 rounded-r-lg">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            ℹ️ Certaines transactions confidentielles sont masquées selon vos permissions.
          </p>
        </div>
      )}

      {/* Grille de widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Widget Stats */}
        {widgets.stats?.visible && (
          <div className={`${widgets.stats.size === 'large' ? 'md:col-span-2' : 'md:col-span-2'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Membres Actifs"
                value={`${activeMembers}/${totalMembers}`}
                icon={Users}
                color="blue"
                trend="+2 ce mois"
              />
              <StatCard
                title="Total Revenus"
                value={formatCurrency(totalRevenues)}
                icon={DollarSign}
                color="green"
                trend="+12% vs mois dernier"
              />
              <StatCard
                title="Total Dépenses"
                value={formatCurrency(totalExpenses)}
                icon={TrendingDown}
                color="red"
                trend="-8% vs mois dernier"
              />
              <StatCard
                title="Solde"
                value={formatCurrency(balance)}
                icon={balance >= 0 ? TrendingUp : TrendingDown}
                color={balance >= 0 ? 'green' : 'red'}
                trend={balance >= 0 ? 'Positif' : 'Négatif'}
              />
            </div>
          </div>
        )}

        {/* Graphique Revenus/Dépenses */}
        <Widget id="revenueChart" title="Évolution Revenus vs Dépenses">
          <div className="h-80">
            <Line data={lineChartData} options={lineChartOptions} />
          </div>
        </Widget>

        {/* Graphique par catégorie */}
        <Widget id="categoryChart" title="Dépenses par Catégorie">
          <div className="h-80">
            <Doughnut data={doughnutChartData} options={doughnutChartOptions} />
          </div>
        </Widget>

        {/* Revenus récents */}
        <Widget id="recentRevenue" title="Revenus Récents">
          <div className="space-y-3">
            {visibleDonations.slice(0, 5).map(donation => (
              <div key={donation.id} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <p className="font-medium text-gray-800 dark:text-white">{donation.libelle}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{donation.type}</p>
                </div>
                <span className="font-bold text-green-600 dark:text-green-400">
                  {formatCurrency(donation.amount)}
                </span>
              </div>
            ))}
            {visibleDonations.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4 text-sm">
                Aucun revenu récent
              </p>
            )}
          </div>
        </Widget>

        {/* Dépenses récentes */}
        <Widget id="recentExpense" title="Dépenses Récentes">
          <div className="space-y-3">
            {visibleExpenses.slice(0, 5).map(expense => (
              <div key={expense.id} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <p className="font-medium text-gray-800 dark:text-white">{expense.libelle}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{expense.category}</p>
                </div>
                <span className="font-bold text-red-600 dark:text-red-400">
                  {formatCurrency(expense.amount)}
                </span>
              </div>
            ))}
            {visibleExpenses.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4 text-sm">
                Aucune dépense récente
              </p>
            )}
          </div>
        </Widget>

        {/* Widget Annonces */}
        <div className="md:col-span-2">
          <DashboardAnnouncements onNavigate={onNavigate} />
        </div>
      </div>
    </div>
  );
};

// Composant StatCard mémoïsé
const StatCard = React.memo(({ title, value, icon: Icon, color, trend }) => {
  const colors = {
    blue: {
      bg: 'bg-blue-500',
      light: 'bg-blue-50 dark:bg-blue-900/20',
      text: 'text-blue-600 dark:text-blue-400'
    },
    green: {
      bg: 'bg-green-500',
      light: 'bg-green-50 dark:bg-green-900/20',
      text: 'text-green-600 dark:text-green-400'
    },
    red: {
      bg: 'bg-red-500',
      light: 'bg-red-50 dark:bg-red-900/20',
      text: 'text-red-600 dark:text-red-400'
    }
  };

  const colorScheme = colors[color];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <div className="flex justify-between items-start mb-4">
        <div className={`p-3 rounded-lg ${colorScheme.light}`}>
          <Icon size={24} className={colorScheme.text} />
        </div>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{title}</p>
      <p className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{value}</p>
      {trend && (
        <p className={`text-xs ${colorScheme.text}`}>
          {trend}
        </p>
      )}
    </div>
  );
});

export default DashboardView;